import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import { api as axios } from '../../../api';
import url from '../../../serverUrl';
import { fmtPrice } from './MarketStatsBar';
import './proTrading.css';

// ---------------------------------------------------------------------------
// Pro order book + live trades panel.
//
// Order Book: /hootdex/orderbook aggregates every resting bid/ask into price
// levels (grouping selectable 1x/10x/100x), asks above the spread (best ask
// nearest the middle), bids below, each with a depth bar sized by cumulative
// size, and a flash on any level whose size changed since the last update.
// Trades: the latest Hootdex fills for the token (/crypto/marketTrade).
// ---------------------------------------------------------------------------

const BOOK_POLL_MS = 2000;
const TRADES_POLL_MS = 2000;
const ROWS = 12;

const fmtSize = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  const d = a >= 1000 ? 0 : a >= 100 ? 2 : a >= 1 ? 3 : 5;
  return n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
};

const fmtCompact = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  if (a >= 1e9) return (n / 1e9).toFixed(2) + 'B';
  if (a >= 1e6) return (n / 1e6).toFixed(2) + 'M';
  if (a >= 1e4) return (n / 1e3).toFixed(1) + 'K';
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

function usePoll(fn, ms, deps) {
  useEffect(() => {
    let cancelled = false;
    let timer;
    const tick = () =>
      Promise.resolve(fn(() => cancelled))
        .catch(() => {})
        .finally(() => {
          if (!cancelled) timer = setTimeout(tick, ms);
        });
    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// Prices in the book use the level's tick precision (81,560 / 99.86), not a
// generic per-magnitude format, so every row lines up.
const fmtFixed = (v, d) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (d == null) return fmtPrice(n);
  return n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
};

function BookRow({ level, side, maxTotal, unit, base, flash, onPick, decimals }) {
  const pct = maxTotal > 0 ? Math.min(100, (level.total / maxTotal) * 100) : 0;
  const size = unit === 'usd' ? level.size * level.price : level.size;
  const total = unit === 'usd' ? level.cumNotional : level.total;
  return (
    <Tooltip
      placement="left"
      arrow
      title={`${level.orders} order${level.orders === 1 ? '' : 's'} · ≈ ${fmtCompact(
        level.size * level.price
      )} USD`}
    >
      <div
        className={`pt-book-row ${flash ? `pt-row-flash-${side}` : ''}`}
        onClick={() => onPick && onPick(level.price, side)}
      >
        <div className={`pt-depth pt-depth-${side}`} style={{ width: `${pct}%` }} />
        <span className={side === 'ask' ? 'pt-down' : 'pt-up'}>
          {fmtFixed(level.price, decimals)}
        </span>
        <span>{unit === 'usd' ? fmtCompact(size) : fmtSize(size)}</span>
        <span>{unit === 'usd' ? fmtCompact(total) : fmtSize(total)}</span>
      </div>
    </Tooltip>
  );
}

export default function OrderBook({ orderSymbol, displaySymbol, onPickPrice, refPrice }) {
  // Last traded price: the server leaves out asks below it / bids above it
  // (orders that can't be resting liquidity) and reports how many.
  const ref = Number(refPrice) > 0 ? Number(Number(refPrice).toPrecision(5)) : undefined;
  const [tab, setTab] = useState('book');
  const [group, setGroup] = useState(1);
  const [unit, setUnit] = useState('base');
  const [book, setBook] = useState(null);
  const [trades, setTrades] = useState([]);
  const [flashes, setFlashes] = useState({});
  const prevSizes = useRef({});
  // A single empty answer during a backend hiccup used to blank the book /
  // tape; only accept "empty" after it repeats (a real empty market still
  // shows within a few seconds).
  const emptyBook = useRef(0);
  const emptyTape = useRef(0);
  const seenTrades = useRef(new Set());
  const [newTrades, setNewTrades] = useState(new Set());
  const base = (displaySymbol || orderSymbol || '').replace(/\/.*$/, '');

  // Reset when the market changes.
  useEffect(() => {
    setBook(null);
    setTrades([]);
    prevSizes.current = {};
    seenTrades.current = new Set();
  }, [orderSymbol, displaySymbol]);

  usePoll(
    (isCancelled) =>
      orderSymbol &&
      axios
        .get(`${url}/hootdex/orderbook`, {
          params: { symbol: orderSymbol, depth: ROWS, group, ref },
          timeout: 8000
        })
        .then((res) => {
          if (isCancelled() || !res.data || res.data.symbol !== orderSymbol) return;
          const next = res.data;
          const hasLevels = (next.bids || []).length || (next.asks || []).length;
          if (!hasLevels && Object.keys(prevSizes.current).length && ++emptyBook.current < 3) return;
          if (hasLevels) emptyBook.current = 0;
          const f = {};
          const sizes = {};
          for (const side of ['bids', 'asks']) {
            for (const l of next[side] || []) {
              const k = `${side}:${l.price}`;
              sizes[k] = l.size;
              const prev = prevSizes.current[k];
              if (prev !== undefined && prev !== l.size) f[k] = true;
              if (prev === undefined && Object.keys(prevSizes.current).length) f[k] = true;
            }
          }
          prevSizes.current = sizes;
          setBook(next);
          setFlashes(f);
        }),
    BOOK_POLL_MS,
    [orderSymbol, group, ref]
  );

  usePoll(
    (isCancelled) =>
      tab === 'trades' &&
      displaySymbol &&
      axios
        .get(`${url}/crypto/marketTrade`, {
          params: { symbol: displaySymbol },
          timeout: 8000
        })
        .then((res) => {
          if (isCancelled() || !Array.isArray(res.data)) return;
          if (!res.data.length && seenTrades.current.size && ++emptyTape.current < 3) return;
          if (res.data.length) emptyTape.current = 0;
          const rows = res.data.slice(0, 60);
          const fresh = new Set();
          if (seenTrades.current.size) {
            for (const t of rows) if (!seenTrades.current.has(t._id)) fresh.add(t._id);
          }
          rows.forEach((t) => seenTrades.current.add(t._id));
          setTrades(rows);
          setNewTrades(fresh);
        }),
    TRADES_POLL_MS,
    [displaySymbol, tab]
  );

  // Cumulative USD per level for the USD view.
  const levels = useMemo(() => {
    const withCum = (arr) => {
      let c = 0;
      return (arr || []).map((l) => {
        c += l.size * l.price;
        return { ...l, cumNotional: c };
      });
    };
    return { bids: withCum(book?.bids), asks: withCum(book?.asks) };
  }, [book]);

  const maxTotal = Math.max(
    levels.bids[levels.bids.length - 1]?.total || 0,
    levels.asks[levels.asks.length - 1]?.total || 0
  );
  // Asks render best-at-bottom so both best prices meet at the spread row.
  const asksDisplay = [...levels.asks].reverse();
  const tick = book?.tick;

  return (
    <div className="pt-book">
      <div className="pt-tabs">
        <button
          type="button"
          className={tab === 'book' ? 'pt-tab pt-tab-active' : 'pt-tab'}
          onClick={() => setTab('book')}
        >
          Order Book
        </button>
        <button
          type="button"
          className={tab === 'trades' ? 'pt-tab pt-tab-active' : 'pt-tab'}
          onClick={() => setTab('trades')}
        >
          Trades
        </button>
      </div>

      {tab === 'book' ? (
        <>
          <div className="pt-book-controls">
            <select
              className="pt-select"
              value={group}
              onChange={(e) => setGroup(Number(e.target.value))}
              aria-label="Price grouping"
            >
              {[1, 10, 100].map((m) => (
                <option key={m} value={m}>
                  {tick ? fmtGroup((tick / group) * m) : `${m}x`}
                </option>
              ))}
            </select>
            <select
              className="pt-select"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              aria-label="Size unit"
            >
              <option value="base">{base || 'Token'}</option>
              <option value="usd">USD</option>
            </select>
          </div>

          <div className="pt-book-head">
            <span>Price</span>
            <span>Size ({unit === 'usd' ? 'USD' : base})</span>
            <span>Total ({unit === 'usd' ? 'USD' : base})</span>
          </div>

          <div className="pt-book-side pt-book-asks">
            {asksDisplay.map((l) => (
              <BookRow
                key={`a${l.price}`}
                level={l}
                side="ask"
                maxTotal={maxTotal}
                unit={unit}
                base={base}
                flash={flashes[`asks:${l.price}`]}
                onPick={onPickPrice}
                decimals={book?.decimals}
              />
            ))}
            {!book && <div className="pt-empty">Loading order book…</div>}
            {book && levels.asks.length === 0 && levels.bids.length > 0 && (
              <div className="pt-empty">No sell orders near the market</div>
            )}
            {book && levels.asks.length === 0 && levels.bids.length === 0 && (
              <div className="pt-empty pt-book-empty">
                No resting orders near the market right now.
                <br />
                Market orders still fill instantly at the live price.
              </div>
            )}
          </div>

          <div className={`pt-spread ${book?.crossed ? 'pt-spread-crossed' : ''}`}>
            {book?.crossed ? (
              <Tooltip
                arrow
                title="The best bid is above the best ask - these resting orders should have matched. Shown as-is."
              >
                <span>Crossed book</span>
              </Tooltip>
            ) : (
              <span>Spread</span>
            )}
            <span>{book?.spread != null ? fmtFixed(book.spread, book.decimals) : '—'}</span>
            <span>{book?.spreadPct != null ? `${book.spreadPct.toFixed(3)}%` : ''}</span>
          </div>

          <div className="pt-book-side">
            {levels.bids.map((l) => (
              <BookRow
                key={`b${l.price}`}
                level={l}
                side="bid"
                maxTotal={maxTotal}
                unit={unit}
                base={base}
                flash={flashes[`bids:${l.price}`]}
                onPick={onPickPrice}
                decimals={book?.decimals}
              />
            ))}
            {book && levels.bids.length === 0 && levels.asks.length > 0 && (
              <div className="pt-empty">No buy orders near the market</div>
            )}
          </div>
          {book && (book.autoQuotes === 0 || (book.lastQuoteAt && Date.now() - book.lastQuoteAt > 3 * 60 * 1000)) && (
            <Tooltip
              arrow
              title="No new quotes have been posted to this book recently, so the levels above are not moving. Trades still settle at the live price."
            >
              <div className="pt-stale-note pt-paused-note">
                {book.autoQuotes === 0 && !book.lastQuoteAt
                  ? 'Market-maker quotes offline'
                  : `Quotes paused · last update ${agoText(book.lastQuoteAt)}`}
              </div>
            </Tooltip>
          )}
          {(book?.staleAsks > 0 || book?.staleBids > 0) && (
            <Tooltip
              arrow
              title="Resting bids and asks that overlap each other would match on execution, so the book shows what remains after matching them."
            >
              <div className="pt-stale-note">
                {(book.staleAsks + book.staleBids).toLocaleString('en-US')} crossing orders netted out
              </div>
            </Tooltip>
          )}
        </>
      ) : (
        <>
          <div className="pt-book-head">
            <span>Price</span>
            <span>Size ({base})</span>
            <span>Time</span>
          </div>
          <div className="pt-trades">
            {trades.map((t) => {
              const buy = t.type === 'BUY';
              return (
                <div
                  key={t._id}
                  className={`pt-book-row ${newTrades.has(t._id) ? `pt-row-flash-${buy ? 'bid' : 'ask'}` : ''}`}
                >
                  <span className={buy ? 'pt-up' : 'pt-down'}>{fmtPrice(t.price)}</span>
                  <span>{fmtSize(t.amount)}</span>
                  <span className="pt-muted">
                    {new Date(t.timestamp).toLocaleTimeString('en-GB', { hour12: false })}
                  </span>
                </div>
              );
            })}
            {trades.length === 0 && <div className="pt-empty">Loading trades…</div>}
          </div>
        </>
      )}
    </div>
  );
}

function agoText(ts) {
  const m = Math.round((Date.now() - ts) / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h}h ${m % 60}m ago` : `${Math.floor(h / 24)}d ago`;
}

function fmtGroup(t) {
  if (!Number.isFinite(t)) return '';
  if (t >= 1) return String(Math.round(t));
  const d = Math.max(0, -Math.floor(Math.log10(t) + 1e-9));
  return t.toFixed(d);
}
